import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertCircle, RefreshCw, PackageCheck, PackageX, HandHeart, Trash2, PauseCircle, Send, ArrowRight } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DISPOSITION_LABEL } from "@/lib/returns";
import { formatDate } from "@/lib/format";

const DISP_ICON = { restock: PackageCheck, damage: PackageX, donate: HandHeart, dispose: Trash2, hold: PauseCircle, send_back: Send };

export default function ActionCenter() {
  const navigate = useNavigate();
  const { setViewAs, isAdmin } = useAuth();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/customer-actions?hours=72")
      .then(({ data }) => setActions(data.actions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(timer.current);
  }, [load]);

  const count = actions.length;
  const goto = (a) => {
    setOpen(false);
    if (isAdmin) {
      setViewAs(a.account_id, a.company_name);
      navigate("/returns");
    } else {
      // Restricted staff (returns manager) work from their console.
      navigate("/admin");
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Action-required bar — lights up when customers have dispositioned returns */}
      <button
        data-testid="action-required-bar"
        onClick={() => setOpen(true)}
        className={`hidden sm:inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold transition-all ${
          count > 0
            ? "bg-bh-orange text-bh-navy-deep shadow-[0_0_0_3px_rgba(249,170,37,0.25)] animate-pulse"
            : "bg-bh-bg text-bh-muted"
        }`}
      >
        <AlertCircle className="w-4 h-4" />
        {count > 0 ? `${count} customer action${count === 1 ? "" : "s"} needed` : "No pending actions"}
      </button>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="notifications-btn"
            className="relative p-2 hover:bg-bh-bg rounded-full transition-colors"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5 text-bh-body" />
            {count > 0 && (
              <span className="absolute top-0.5 right-0.5 bg-bh-orange text-bh-navy-deep text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0" data-testid="action-center-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-bh-divider">
            <div>
              <p className="text-sm font-bold text-bh-navy-deep" style={{ fontFamily: "Manrope" }}>Customer actions</p>
              <p className="text-xs text-bh-muted">Dispositions set by brands in the last 3 days</p>
            </div>
            <button onClick={load} data-testid="action-refresh-btn" className="p-1.5 hover:bg-bh-bg rounded-md text-bh-muted">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {count === 0 ? (
              <p className="text-sm text-bh-muted text-center py-10 px-4">You're all caught up. Nothing needs action right now.</p>
            ) : (
              actions.map((a, i) => {
                const Icon = DISP_ICON[a.disposition] || PackageCheck;
                return (
                  <button
                    key={`${a.return_id}-${i}`}
                    onClick={() => goto(a)}
                    data-testid="action-item"
                    className="w-full text-left flex items-center gap-3 px-4 py-3 border-b border-bh-divider/60 hover:bg-bh-bg transition-colors group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-bh-orange/15 text-bh-orange flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-bh-navy-deep truncate">
                        {a.company_name} · <span className="font-mono">{a.order_number}</span>
                      </p>
                      <p className="text-xs text-bh-muted truncate">
                        {DISPOSITION_LABEL(a.disposition)} — {a.item_name} · {formatDate(a.dispositioned_at)}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-bh-muted opacity-0 group-hover:opacity-100 shrink-0" />
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
